<template>
  <div id="peer_assessment_modal" class="peer-assmnt-modal modal fade">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal__featured-header">
          <h4 class="modal__featured-header_title">
            <span>
              {{ peerEntryIndex + 1 }}/{{ $parent.peerEntries.length }}
            </span>
            {{ translated('peer_assessments') }}
          </h4>

          <span
            v-if="!showOnboardingScreen"
            class="additional-info--with-icon"
            @click="restartTour"
            :tooltip="tooltipParams(4, 'bottom', true)"
          >
            <span class="kf-stack kf-stack--circle">
              <i class="kf kf-circle-fill" />
              <i class="kf kf-inverse kf-info" />
            </span>
          </span>
        </div>

        <div class="modal__body--full">
          <div
            class="peer-assmnt-intro"
            v-if="showOnboardingScreen"
          >
            <div class="peer-assmnt-intro__icon">
              <span class="kf-stack kf-stack--circle">
                <i class="kf kf-circle-outline" />
                <i class="kf kf-peer_assessment" />
              </span>
            </div>

            <div class="peer-assmnt-intro__headline">
              {{ translated('peer_assessment_modal_intro_headline') }}
            </div>

            <div class="peer-assmnt-intro__subhead">
              {{ translated('peer_assessment_modal_intro_subhead') }}
            </div>

            <div class="peer-assmnt-intro__button">
              <a
                class="btn--md btn--dark"
                href='javascript:;'
                @click="startPeerAssessments"
              >
                {{ translated('peer_assessment_modal_intro_button') }}
              </a>
            </div>

            <div class="peer-assmnt-intro__skip">
              <a
                class="inline-block-link"
                href='javascript:;'
                @click="skipTour"
              >
                {{ translated('peer_assessment_modal_intro_skip') }}
              </a>
            </div>
          </div>

          <div v-else class="peer-assmnt">
            <div class="peer-assmnt__student-info">
              <div class="peer-assmnt__student_header_student-select">
                <div
                  class="peer-assmnt__student_header_student-select_back"
                  :class="{ disabled: isFirstPeerEntry }"
                >
                  <a
                    class="kd-ibtn"
                    @click="previousEntry"
                  >
                    <i class="kf kf-arrow-thin kf-flip-horizontal" />
                  </a>
                </div>

                <div class="peer-assmnt__student_header_student-select_avatar">
                  <img
                    class="avatar-circle--sm"
                    :src="avatarSrc"
                    :alt="I18n.t('dict.avatar')"
                  />
                </div>

                <div
                  class="peer-assmnt__student_header_student-select_forward"
                  :class="{ disabled: isLastPeerEntry }"
                  :tooltip="tooltipParams(1, 'right')"
                >
                  <a
                    class="kd-ibtn"
                    @click="nextEntry"
                  >
                    <i class="kf kf-arrow-thin" />
                  </a>
                </div>
              </div>

              <div class="peer-assmnt__student-detail">
                <div class="peer-assmnt__student-detail_title">
                  <div class="peer-assmnt__student-detail_subtitle">
                    {{ translated('peer_assessments') }} {{ peerEntryIndex + 1 }}
                  </div>
                </div>

                <div class="peer-assmnt__student-detail_submitted-files">
                  <div class="peer-assmnt__submitted-files__header Submitted Files">
                    <div
                      class="peer-assmnt__submitted-files__list"
                      :tooltip="tooltipParams(2, 'right')"
                    >
                      <filebox
                        v-for="coursework_field_entry in currentPeerEntry.coursework_field_entries"
                        :key="coursework_field_entry.id"
                        :data="coursework_field_entry"
                        :callbacks="{ play: previewFieldEntry, preview: previewFieldEntry }"
                        template="#peer_assessment_filebox_template"
                      ></filebox>
                    </div>
                  </div>
                </div>
              </div>

              <div class="peer-assmnt__student-detail_btns">
                <a
                  class="btn--md btn--del btn--outline"
                  href='javascript:;'
                  @click="cancel"
                >
                  {{ I18n.t('layouts.exit') }}
                </a>

                <a
                  class="btn--md btn--save"
                  href='javascript:;'
                  @click="save(true)"
                >
                  {{ translated('submit') }}
                </a>
              </div>
            </div>

            <div class="peer-assmnt__grading-info">
              <div
                v-if="previewStage === null"
                class="peer-assmnt__grading_header"
              >
                <div class="peer-assmnt__grading_header_title">
                  {{ translated('grade_the_assignment') }}
                </div>

                <div
                  v-if="showAssessmentHelp"
                  class="peer-assmnt__grading_header_description"
                >
                  <span>
                    Please refer to the "Need Help?" section below if you desire
                    guidance in assessing your fellow students' work
                  </span>

                  <div
                    class="peer-assmnt__grading_header_addl-mtl kd-expandable-text"
                    :expandabletext="true"
                  >
                    <div class="kd-expandable-text__prompt prompt">
                      <a href="javascript:;" :tabkeyfocus="true">
                        <span class="kd-expandable-text__prompt_icon answer-icon" />
                        <span class="kd-expandable-text__prompt_text question__text">
                          Need Help?
                        </span>
                      </a>

                      <div class="kd-expandable-text__prompt_content answer">
                        <p>
                          We believe that one of the best ways to learn is from
                          each other. Peer Assessments are a great way to respectfully
                          engage with your peers on their coursework.
                        </p>

                        <div
                          v-if="grading_criterion.scope == 'peer' && (grading_criterion.notes !== '')"
                          :expandabletext="true"
                          v-for="grading_criterion in coursework.grading_criteria"
                          :key="grading_criterion.id"
                          class="kd-expandable-text"
                        >
                          <div class="kd-expandable-text__prompt prompt">
                            <a href="javascript:;">
                              <span class="kd-expandable-text__prompt_icon answer-icon" />
                              <span class="kd-expandable-text__prompt_text question__text">
                                How do I grade {{ grading_criterion.name }}
                              </span>
                            </a>
                          </div>

                          <div class="kd-expandable-text__prompt_content answer">
                            {{ grading_criterion.notes }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="peer-assmnt__grading_content"
                  :tooltip="tooltipParams(3, 'top')"
                >
                  <div class="kd-panel">
                    <div class="kd-panel__featured-header">
                      <div class="kd-panel__featured-header_title">
                        Grading Criteria
                      </div>
                    </div>

                    <div class="kd-panel__content">
                      <div class="kd-panel__content_row">
                        <peer-assessment-criterion
                          v-for="grading_criterion in currentPeerEntry.grading_criteria"
                          :grading_criterion="grading_criterion"
                          :key="grading_criterion.id"
                          ref="peer-assessment-criteria-panel"
                        ></peer-assessment-criterion>

                        <peer-assessment-feedback
                          :peer_entry="currentPeerEntry"
                          ref="peer-assessment-feedback-panel"
                        ></peer-assessment-feedback>
                      </div>

                      <div class="kd-panel__content_row">
                        <div class="peer-assmnt__grading_footer">
                          <div class="peer-assmnt__grading_footer_prev">
                            <a
                              class="peer-assmnt__grading_footer_prev_link"
                              href='javascript:;'
                              @click="previousEntry"
                              :class="{ 'is-disabled': isFirstPeerEntry }"
                            >
                              <i class="kf kf-arrow-thin kf-rotate-180" />
                              Previous Assessment
                            </a>
                          </div>

                          <div class="peer-assmnt__grading_footer_next">
                            <a
                              class="peer-assmnt__grading_footer_next_link"
                              href="javascript:;"
                              @click="nextEntry"
                              :class="{ 'is-disabled': isLastPeerEntry }"
                            >
                              Next Assessment
                              <i class="kf kf-arrow-thin" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="peer-assmnt__preview_back-btn">
                <a
                  class="kd-ibtn"
                  href='javascript:;'
                  @click="previewStage = null"
                >
                  <i class="kf kf-back-arrow" aria-label='Back' />
                </a>

                <div class="peer-assmnt__preview_content">
                  <file-preview :data="previewStage"></file-preview>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'underscore'
import PeerAssessmentCriterion from './criterion.vue';
import PeerAssessmentFeedback from './feedback.vue';
import FilePreview from './file-preview';

export default {
  props: ['avatarSrc', 'coursework', 'extra_credit'],

  data() {
    return {
      peerEntryIndex: 0,
      tooltips: [],
      previewStage: null,
      showOnboardingScreen: !this.extra_credit,
      tourDisabled: false,
    };
  },

  components: {
    PeerAssessmentCriterion,
    PeerAssessmentFeedback,
    FilePreview
  },

  computed: {
    currentPeerEntry() {
      return this.$parent.peerEntries[this.peerEntryIndex];
    },

    isFirstPeerEntry() {
      return this.peerEntryIndex === 0;
    },

    isLastPeerEntry() {
      return (this.peerEntryIndex + 1) === this.$parent.peerEntries.length;
    },

    showAssessmentHelp() {
      return _.any(
        this.coursework.grading_criteria,
        crit => crit.scope === 'peer' && !_.isEmpty(crit.notes)
      );
    }
  },

  methods: {
    translated(key) {
      return I18n.t(`coursework.student.show_templates.${key}`)
    },

    nextEntry() {
      if (this.isDirty() && this.isValid()) {
        this.save(false).done(this.getNext)
      } else {
        this.getNext()
      }
    },

    getNext() {
      if (!this.isLastPeerEntry) {
        this.resetState()
        this.peerEntryIndex += 1
      }
    },

    previousEntry() {
      if (this.isDirty() && this.isValid()) {
        this.save(false).done(this.getPrev)
      } else {
        this.getPrev()
      }
    },

    getPrev() {
      if (!this.isFirstPeerEntry) {
        this.resetState()
        this.peerEntryIndex -= 1
      }
    },

    cancel() {
      $(this.$el).modal('hide');
    },

    isValid() {
      this.$refs['peer-assessment-criteria-panel'].forEach(crit => {
        crit.$validator.validateAll()
      });

      return !_.any(
        this.$refs['peer-assessment-criteria-panel'],
        crit => crit.errors.any()
      );
    },

    isDirty() {
      return _.any(
        this.$refs['peer-assessment-criteria-panel'],
        function (crit) {
          return _.any(
            crit.fields,
            function (field) {
              return field.dirty;
            }
          );
        }
      );
    },

    resetState() {
      this.previewStage = null

      this.$refs['peer-assessment-criteria-panel'].forEach(crit => {
        crit.$validator.reset();
        crit.editing = null;
      })
    },

    save(closeOnComplete) {
      let self = this;
      let method = 'POST';

      let url = Kadenze.Routes.course_coursework_grades_path(
        this.coursework.course_id,
        this.coursework.id
      );

      let entry = this.currentPeerEntry;

      let data = {grade: entry.grade};

      data.grade.grade_entries_attributes = [];
      delete data.grade.grade_entries;

      _.each(entry.grading_criteria, function(gc, index) {
        let grade_entry = gc.grade_entry;
        data.grade.grade_entries_attributes.push(grade_entry);
      });

      var feedbackBlock = $('.peer-assmnt__grading_criteria-item');

      data.grade.status = 'complete';

      var feedbackElement = $('.vue-quill-editor-start-notes-editing').find('.ql-editor');
      var isPreviewFeedbackEmpty = feedbackBlock.find('.grade-feedback-editor__preview_copy').text() === '';

      var isFeedbackEmpty = feedbackElement.text() === '' && isPreviewFeedbackEmpty;

      if (this.isValid() && !isFeedbackEmpty) {
        let response = new Kadenze.AjaxRequest({
          type: method,
          data: data,
          url: url
        });

        response.done(function (response, status, xhr) {
          //Update the time
          if (self.extra_credit)
            self.coursework.last_extra_peer_assessment_date = new Date().toString();
          else
            self.coursework.last_peer_assessment_date = new Date().toString();

          //Update the grade
          self.currentPeerEntry.grade.id = xhr.responseJSON.grade.id;
          self.currentPeerEntry.grade.status = xhr.responseJSON.grade.status;
          self.currentPeerEntry.grade.created_at = xhr.responseJSON.grade.created_at;

          //Iterate over all grade entries and update their id & grade_id
          _.each(xhr.responseJSON.grade.grade_entries, function (entry_data) {
            let gc = _.find(self.currentPeerEntry.grading_criteria, function (gc) {
              return gc.id === entry_data.grading_criterion_id;
            });

            if (!_.isEmpty(gc)) {
              gc.grade_entry.id = entry_data.id;
              gc.grade_entry.grade_id = entry_data.grade_id;
            }
          });

          new Kadenze.Notice.success({
            message: `Submitted peer assessment ${self.peerEntryIndex + 1}`
          });

          if (closeOnComplete && self.$parent.currentStatus === 'complete') {
            if (!self.extra_credit)
              self.$parent.getPeerEntries(true);

            self.cancel();
          }
        });

        response.fail(function(xhr, status, msg) {
          new Kadenze.Notice.error(xhr.responseJSON);
        });

        return response;
      } else {
        if(feedbackElement) {
          feedbackBlock.find('.grade-feedback-editor__preview_edit-btn').trigger('click');

          $('.quill-required-error').removeClass('hide');

          feedbackElement.addClass('ql-missing-field-error');
        }

        new Kadenze.Notice.error({
          message: `Please complete all required fields in order to submit peer assessment ${self.peerEntryIndex + 1}`
        });
      }
    },

    tooltipParams(order, position, end) {
      return {
        disabled: this.tourDisabled,
        eventIn: null,
        position: (position || 'bottom'),
        arrow: true,
        template: `templates/tooltips/student/peer_assessment/tour${order}`,
        interactive: {
          context: 'StudentPeerAssessment',
          order: order,
          end: (end || false),
          events: {
            onShow: this.tooltipShown
          }
        }
      };
    },

    tooltipShown(tooltip) {
      let self = this;

      $(window).trigger('resize');

      if (!$(tooltip.template).is(':visible')) {
        setTimeout(function() { self.tooltipShown(tooltip); }, 100);
      } else {
        this.tooltips.push(tooltip);
      }
    },

    startPeerAssessments() {
      this.showOnboardingScreen = false;
    },

    skipTour() {
      this.tourDisabled = true;

      Kadenze.Tooltip.prototype.resetCurrentPos('StudentPeerAssessment', 999);

      _.extend(this.$parent.userState, {tooltips: {'StudentPeerAssessment': 999}});

      this.startPeerAssessments();
    },

    restartTour() {
      this.tourDisabled = false;

      _.each(this.tooltips, function (tooltip) {
        tooltip.interactiveHide();
      });

      Kadenze.Tooltip.prototype.resetCurrentPos('StudentPeerAssessment');
    },

    previewFieldEntry(file) {
      this.previewStage = file;
    }
  },

  created() {
    if (
      this.$parent.userState &&
        this.$parent.userState.tooltips &&
          this.$parent.userState.tooltips['StudentPeerAssessment'] >= 0
    )
      this.showOnboardingScreen = false;
  },

  mounted() {
    const dict = {
      attributes: {
        notes: 'feedback',
        value: 'grade'
      }
    };

    this.$validator.localize('en', dict);

    $(this.$el).on('shown.bs.modal', function (e) {
      $(this).appendTo('body');
    });

    $(this.$el).on('hidden.bs.modal', e => {
      this.$parent.showPeerAssessmentModal = false;
    });

    $(this.$el).modal('show');
  }
};
</script>
